import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { accountsAPI, categoriesAPI } from '@/utils/api';
import { toast } from 'sonner';

const CategoryPage = () => {
  const { slug } = useParams();
  const [category, setCategory] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [slug]);

  const loadData = async () => {
    try {
      const categoriesRes = await categoriesAPI.getAll();
      const foundCategory = categoriesRes.data.find(c => c.slug === slug);
      
      if (!foundCategory) {
        toast.error('Category not found');
        return;
      }
      
      setCategory(foundCategory);
      
      const accountsRes = await accountsAPI.getAll({ category: foundCategory.category_id });
      setAccounts(accountsRes.data);
    } catch (error) {
      toast.error('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="category-page">
      <Navbar />
      
      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          {category && (
            <div className="mb-12">
              <h1 className="text-4xl sm:text-5xl font-heading font-black tracking-tight mb-4" data-testid="category-title">
                {category.name}
              </h1>
              <p className="text-muted-foreground text-lg">{category.description}</p>
            </div>
          )}
          
          {accounts.length === 0 ? (
            <div className="text-center py-16" data-testid="no-accounts-message">
              <p className="text-muted-foreground text-lg">No accounts available in this category yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="accounts-grid">
              {accounts.map((account) => (
                <Link
                  key={account.account_id}
                  to={`/account/${account.account_id}`}
                  className="group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-white/5 to-transparent hover:border-primary/50 transition-all duration-300 product-card-hover"
                  data-testid={`account-card-${account.account_id}`}
                >
                  <div className="aspect-video overflow-hidden bg-secondary">
                    <img
                      src={account.image_url}
                      alt={account.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="font-heading font-bold text-lg">{account.title}</h3>
                      {account.stock > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-accent/10 text-accent text-xs font-bold">
                          <Check className="w-3 h-3" />
                          In Stock
                        </span>
                      )}
                    </div>
                    
                    <p className="text-muted-foreground text-sm mb-4 line-clamp-2">{account.description}</p>
                    
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-xs px-2 py-1 rounded-md bg-white/5 border border-white/10">
                        Valid for {account.validity_days} days
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-heading font-black">₹{account.price}</span>
                      <Button size="sm" className="btn-primary-glow rounded-full">
                        View Details
                      </Button>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default CategoryPage;
